import { useEffect, useState } from "react";

import { useLocation, useNavigate, useParams } from "react-router-dom";

import useNotification from "../../components/notifications/useNotifications";
import Breadcrumbs from "../../components/shared/Breadcrumbs/Breadcrumbs";
import LoadingWheel from "../../components/shared/LoadingWheel";
import VehicleDetails from "../../components/vehicles/VehicleDetails";
import { usePageNameContext } from "../../contexts/PageTitleContext";

import useSingleQuote from "../../data/useSingleQuote";

import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import getAddress from "../../utils/getAddress";
import { IQuote, QuoteType, Status } from "../../models/Quote";
import Card from "../../components/shared/Card/Card";
import QuoteCalculationCard from "../../components/quotes/QuoteCalculationCard";
import QuoteCalculationJsonCard from "../../components/quotes/QuoteCalculationJsonCard";
import QuoteContactDetails from "../../components/quotes/QuoteContactDetails";
import QuoteDetails from "../../components/shared/Quotes/QuoteDetails";
import ActionButtons from "../../components/shared/Actions/ActionButtons";
import ActionButton from "../../components/shared/Actions/ActionButton";
import AmendPriceModal from "../../components/shared/Quotes/AmendPriceModal";
import { ArrowRightCircleIcon, BeakerIcon, CodeBracketIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import PriceTag from "../../components/shared/PriceTag";
import useConvertQuoteToLead from "../../data/useConvertQuoteToLead";
import ConvertToLeadModal from "../../components/shared/Quotes/ConvertToLeadModal";
import Pill from "../../components/shared/Pill";
import openInNewTab from "../../utils/openInNewTab";
import useLeadsByQuote from "../../data/useLeadsByQuote";
import { useAuth } from "../../contexts/AuthContext";
import { wilmaSAVCustomerId } from "../../config";
import QuoteOwnerPill from "../../components/shared/Quotes/QuoteOwnerPill";
import QuoteWebsitePill from "../../components/shared/Quotes/QuoteWebsitePill";
import useQuoteAmends from "../../data/useQuoteAmends";
import SimulatePricesModal from "../../components/quotes/breakdown/SimulatePricesModal";
import QuoteBreakdownModal from "../../components/quotes/breakdown/QuoteBreakdownModal";

export default function Quote() {
    const navigate = useNavigate();
    const { addNotification } = useNotification();
    const { saveHandlers } = useDefaultCRUDHandlers("Quote");
    const { setInfo } = usePageNameContext();
    const { quoteId, tab } = useParams();
    const { state } = useLocation();
    const { quote: singleQuote } = useSingleQuote(quoteId!);
    const [quote, setQuote] = useState<IQuote | undefined>();
    const [loading, setLoading] = useState(true);
    const [quoteName, setQuoteName] = useState<string | undefined>("");
    const [open, setOpen] = useState(false);    

    const { leads } = useLeadsByQuote(quoteId!);

    const leadList = leads.data?.data ?? [];
    const hasLead = leadList.length > 0;
    const leadId = leadList[0]?.id;

    const [amendOpen, setAmendOpen] = useState(false);
    const { amendPrice, amendContact, amendPayment } = useQuoteAmends(quoteId!);

    const [convertOpen, setConvertOpen] = useState(false);
    const { convert } = useConvertQuoteToLead(quoteId!);

    const { customerId } = useAuth();
    // Not while impersonating: the API sees the impersonated customer and would refuse.
    const isSav = customerId === wilmaSAVCustomerId;

    const [simulateOpen, setSimulateOpen] = useState(false);
    const [breakdownOpen, setBreakdownOpen] = useState(false);

    const handleAmend = async (value: number) => {
        try {
            await amendPrice.mutateAsync(value);
            addNotification({
                variant: "success",
                primaryText: "Price amended",
            });
            setAmendOpen(false);
        } catch (e: any) {
            addNotification({
                variant: "error",
                primaryText: e?.message ?? "Failed to amend price",
            });
        }
    };

    const handleAmendContact = async (amendedContact: { fullname: string; email: string; phone: string }) => {
        try {
            await amendContact.mutateAsync(amendedContact);
            addNotification({
                variant: "success",
                primaryText: "Contact details amended",
            });
        } catch (e: any) {
            addNotification({
                variant: "error",
                primaryText: e?.message ?? "Failed to amend contact details",
            });
        }
    };

    const handleAmendPayment = async (amendedPayment: { bankAccountName: string, accountNumber: string, sortCode: string }) => {
        try {
            await amendPayment.mutateAsync(amendedPayment);
            addNotification({
                variant: "success",
                primaryText: "Payment details amended",
            });
        } catch (e: any) {
            addNotification({
                variant: "error",   
                primaryText: e?.message ?? "Failed to amend payment details",
            });
        }
    };
        
    useEffect(() => {
        if (singleQuote.isLoading) return;
        if (!singleQuote.data) return;

        setQuote(singleQuote.data.data);
        setLoading(false);
        setQuoteName(singleQuote.data.data.vehicleRegistration);
        setInfo({
            name: `${singleQuote.data.data.vehicleRegistration} - ${singleQuote.data.data.postcode}`,
            desc: `View and manage quote details for ${singleQuote.data.data.vehicleRegistration}`,
        });
    }, [singleQuote.isLoading, singleQuote.data]);

    const pages = [
        { to: "..", label: "Abandoned Quotes", current: false },
        {
          to: quoteId && quote ? `/abandoned-quotes/${quoteId}` : "",
          label: quoteId && quote ? `Quote for ${quote.vehicleRegistration}` : "New Quote",
          current: true,
        },
    ];
    
    if (loading || !quote) return <LoadingWheel />;

    const address = getAddress(quote?.vehicle!);

    const ownerCustomerId =
        quote.assignedCustomerId ??
        quote.sourceCustomerId ??
        quote.collectedCustomerId ??
        quote.dropOffCustomerId ??
        null;

    // With nobody assigned and no source customer, the id above is just whoever won the price -
    // SAV holds marketplace quotes, so calling that yard the owner is misleading.
    const ownerIsPriceWinner = !quote.assignedCustomerId && !quote.sourceCustomerId;

    return (
        <>
            <Breadcrumbs pages={pages} />

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mt-4">
                <ActionButtons>
                    <ActionButton
                        label="Amend price"
                        Icon={PencilSquareIcon}
                        colour="orange"
                        onClick={() => {
                            if (hasLead) return;
                            setAmendOpen(true);
                        }}
                        disabled={hasLead}
                    />
                    <ActionButton
                        label="Convert to lead"
                        Icon={ArrowRightCircleIcon}
                        colour="green"
                        onClick={() => {
                            if (hasLead) return;
                            setConvertOpen(true);
                        }}
                        disabled={hasLead}
                    />
                    {isSav && (
                        <>
                            <ActionButton
                                label="Simulate prices"
                                Icon={BeakerIcon}
                                colour="purple"
                                onClick={() => setSimulateOpen(true)}
                            />
                            <ActionButton
                                label="Quote breakdown"
                                Icon={CodeBracketIcon}
                                colour="blue"
                                onClick={() => setBreakdownOpen(true)}
                            />
                        </>
                    )}
                </ActionButtons>

                <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                    {/* show only when amended */}
                    {quote.amendedPrice != null && (
                        <PriceTag label="Amended" value={quote.amendedPrice} tone="orange" />
                    )}

                    {hasLead && leadId && (
                        <Pill
                        label="Converted to lead"
                        tone="green"
                        onClick={() => openInNewTab(`/leads/${leadId}`)} // adjust route
                        />
                    )}

                    {customerId === wilmaSAVCustomerId && (
                        <QuoteOwnerPill
                            customerId={ownerCustomerId}
                            label={ownerIsPriceWinner ? "Price winner" : "Owner"}
                        />
                    )}
                    {(
                        customerId             === wilmaSAVCustomerId || 
                        quote.sourceCustomerId === customerId         || 
                        quote.sourceCustomerId === null               || 
                        quote.sourceCustomerId === wilmaSAVCustomerId
                    ) && (
                        <QuoteWebsitePill
                            sourceCustomerId={quote.sourceCustomerId ?? null}
                            sourceWebsiteId={quote.sourceWebsiteId ?? null}
                        />
                    )}
                </div>
            </div>

            <AmendPriceModal
                open={amendOpen}
                setOpen={setAmendOpen}
                initialValue={quote.amendedPrice}
                collectionPrice={quote.priceCollected} 
                dropOffPrice={quote.priceDropOff}
                isLoading={amendPrice.isLoading}
                onConfirm={(value) =>
                    amendPrice.mutate(value, {
                        onSuccess: () => setAmendOpen(false),
                    })
                }
            />

            {isSav && (
                <>
                    <SimulatePricesModal quoteId={quote.id} open={simulateOpen} setOpen={setSimulateOpen} />
                    <QuoteBreakdownModal quote={quote} open={breakdownOpen} setOpen={setBreakdownOpen} />
                </>
            )}

            <ConvertToLeadModal
                open={convertOpen}
                setOpen={setConvertOpen}
                quote={quote}
                isLoading={convert.isLoading}
                isSav={customerId === wilmaSAVCustomerId}
                onConfirm={(acceptedAs, choice, assignedCustomerId) => {
                    convert.mutate({ acceptedAs, choice, assignedCustomerId }, {
                    onSuccess: () => {
                        setConvertOpen(false);
                        addNotification({
                            variant: "success",
                            primaryText: "Converted to lead",
                        });
                        // optionally navigate somewhere
                    },
                    onError: () => {
                        addNotification({
                            variant: "error",
                            primaryText: "Failed to convert quote to lead",
                        });
                    },
                });
                }}
                onAmendPrice={handleAmend}
                onAmendContact={handleAmendContact}
                onAmendPayment={handleAmendPayment}
            />

            <div className="mt-4 flex flex-col md:grid md:grid-cols-3 gap-6">
                {/* -------------------------------- */}
                {/* ------------- LEFT ------------- */}
                {/* -------------------------------- */}
                <div className="col-span-1 gap-6 flex flex-col">
                    <div className="col-span-1 bg-white rounded-lg shadow">
                        <iframe
                            className="min-h-[50vh] w-full"
                            title="Google map"
                            style={{ border: 0, borderRadius: "0.5rem" }}
                            loading="lazy"
                            src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyAJWEQovZtn6KkghJ5z6maiL0G6KHjwbS4&q=${encodeURIComponent(
                                address || quote?.postcode || ""
                            )}&zoom=9`}
                            width="100%"
                            height="100%"
                        ></iframe>
                    </div>
                    <VehicleDetails vehicle={quote?.vehicle!} />
                    <QuoteCalculationJsonCard
                        collectedCalculationJson={quote.collectedCalculationJson}
                        dropOffCalculationJson={quote.dropOffCalculationJson}
                    />
                </div>

                {/* -------------------------------- */}
                {/* ------------- RIGHT ------------ */}
                {/* -------------------------------- */}
                <div className="col-span-2 gap-6 flex flex-col">
                    {/* ----- Contact ----- */}
                    {quote.contact && (<QuoteContactDetails contact={quote?.contact} />)}
                    {/* ----- Location ---- */}
                    <Card title="Vehicle Pickup Details" bodyClassName="p-4">
                        <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                            <div className="sm:col-span-1">
                                <label className="text-sm font-medium text-gray-500">
                                    Postcode
                                </label>
                                <input
                                    disabled={true}
                                    type="text"
                                    className={`w-full mt-1 text-sm text-gray-900 border-1 border-gray-400`}
                                    value={quote?.postcode}
                                />
                            </div>
                            <div className="sm:col-span-1">
                                <label className="text-sm font-medium text-gray-500">
                                    Type
                                </label>
                                <input
                                    disabled={true}
                                    type="text"
                                    className={`w-full mt-1 text-sm text-gray-900 border-1 border-gray-400`}
                                    value={
                                        quote?.status == Status.AcceptedCollection
                                        ? "Collection"
                                        : quote?.status == Status.AcceptedDropOff ? "Drop Off" : ""
                                    }
                                />
                            </div>
                        </div>
                    </Card>
                    {/* ----- Payment ---- */}
                    <Card title="Contact Payment Details" bodyClassName="p-4">
                        <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-3">
                            <div className="sm:col-span-1">
                                <label className="text-sm font-medium text-gray-500">
                                    Account Name
                                </label>
                                <input
                                    disabled={true}
                                    type="text"
                                    className={`w-full mt-1 text-sm text-gray-900 border-1 border-gray-400`}
                                    value={quote?.bankAccountName}
                                />
                            </div>
                            <div className="sm:col-span-1">
                                <label className="text-sm font-medium text-gray-500">
                                    Account Number
                                </label>
                                <input
                                    disabled={true}
                                    type="text"
                                    className={`w-full mt-1 text-sm text-gray-900 border-1 border-gray-400`}
                                    value={
                                        quote?.accountNumber
                                    }
                                />
                            </div>
                            <div className="sm:col-span-1">
                                <label className="text-sm font-medium text-gray-500">
                                    Sort Code
                                </label>
                                <input
                                    disabled={true}
                                    type="text"
                                    className={`w-full mt-1 text-sm text-gray-900 border-1 border-gray-400`}
                                    value={quote?.sortCode}
                                />
                            </div>
                        </div>
                    </Card>
                    {/* -------------------- */}
                    <QuoteDetails 
                        quote={quote} 
                        cols="2xl:grid-cols-4 xl:grid-cols-3 md:grid-cols-2" 
                    />
                    {/* -------------------- */}
                    <div className="grid grid-cols-1 gap-x-4 gap-y-4 lg:grid-cols-2">
                        {/* customer id is null if no current customer */}
                        {quote.collectedCustomerId && (
                            <QuoteCalculationCard
                                calculationJson={quote.collectedCalculationJson}
                                quoteType={quote.collectedType}
                                isCollection={true}
                                total={quote.priceCollected}
                            />
                        )}

                        {quote.dropOffCustomerId && (
                            <QuoteCalculationCard
                                calculationJson={quote.dropOffCalculationJson}
                                quoteType={quote.dropOffType}
                                isCollection={false}
                                total={quote.priceDropOff}
                            />
                        )}
                    </div>        
                </div>
            </div>
        </>
    );
}